import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DocumentGenerateDialogProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  unaId: string | null;
  taskTitle: string;
  categoryType: string;
  onGenerated: () => void;
  initialTemplateId?: string;
  phaseRequirement?: {
    template_key?: string;
    document_name?: string;
    description?: string;
    auto_generate?: boolean;
    requires_signature?: boolean;
  };
  autoSubmit?: boolean;
}

export function DocumentGenerateDialog({ 
  open, 
  onClose, 
  taskId, 
  unaId, 
  taskTitle,
  categoryType,
  onGenerated,
  initialTemplateId,
  phaseRequirement,
  autoSubmit = false
}: DocumentGenerateDialogProps) {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      setAutoSelectAttempted(false);
      setSelectedTemplate(null);
      setSelectedTemplateId("");
      loadTemplates();
      if (initialTemplateId) {
        resolveTemplateId(initialTemplateId);
      }
    }
  }, [open, initialTemplateId]);

  const resolveTemplateId = async (templateId: string) => {
    // Check if it's already a UUID
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(templateId);
    
    if (isUUID) {
      console.log('✅ Template ID is already a UUID:', templateId);
      setSelectedTemplateId(templateId);
      return;
    }
    
    // It's a template_key string, need to resolve to UUID
    console.log('🔍 Resolving template_key to UUID:', templateId);
    try {
      const { data: template } = await supabase
        .from('document_template_library')
        .select('id, template_name, template_key')
        .eq('template_key', templateId)
        .eq('is_active', true)
        .maybeSingle();
      
      if (template) {
        console.log('✅ Resolved template:', { id: template.id, name: template.template_name });
        setSelectedTemplateId(template.id);
        setSelectedTemplate(template);
      } else {
        console.warn('⚠️ Could not resolve template_key:', templateId);
        setSelectedTemplateId(templateId); // Fallback to original
      }
    } catch (error) {
      console.error('❌ Error resolving template:', error);
      setSelectedTemplateId(templateId); // Fallback to original
    }
  };

  // Auto-select template based on phase requirement
  useEffect(() => {
    if (open && phaseRequirement?.template_key && templates.length > 0 && !autoSelectAttempted) {
      autoSelectTemplate();
    }
  }, [open, phaseRequirement, templates, autoSelectAttempted]);

  // Auto-submit when template is resolved and autoSubmit is enabled
  useEffect(() => {
    if (autoSubmit && selectedTemplateId && !loading && templates.length > 0) {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedTemplateId);
      if (isUUID) {
        console.log('🚀 Auto-submitting document generation for template:', selectedTemplateId);
        generateDocument();
      }
    }
  }, [autoSubmit, selectedTemplateId, loading, templates]);

  const autoSelectTemplate = async () => {
    if (!phaseRequirement?.template_key) return;
    
    setAutoSelectAttempted(true);
    console.log('🎯 Auto-selecting template with key:', phaseRequirement.template_key);
    
    try {
      // Try library templates first
      const libraryResult = await supabase
        .from('document_template_library')
        .select('id, template_name, template_key, merge_field_definitions')
        .eq('template_key', phaseRequirement.template_key)
        .eq('is_active', true)
        .maybeSingle();
      
      if (libraryResult.data) {
        console.log('✅ Auto-selected library template:', libraryResult.data.template_name);
        setSelectedTemplate(libraryResult.data);
        setSelectedTemplateId(libraryResult.data.id);
        return;
      }
      
      // Fallback to legacy templates
      const legacyResult = await supabase
        .from('document_templates')
        .select('id, template_name')
        .eq('is_active', true)
        .ilike('template_name', `%${phaseRequirement.document_name}%`)
        .maybeSingle();
      
      if (legacyResult.data) {
        console.log('✅ Auto-selected legacy template:', legacyResult.data.template_name);
        setSelectedTemplate(legacyResult.data);
        setSelectedTemplateId(legacyResult.data.id);
      } else {
        console.warn('⚠️ No template found for key:', phaseRequirement.template_key);
      }
    } catch (error) {
      console.error('Error auto-selecting template:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      // Load both library and legacy templates
      const { data: libraryData } = await supabase
        .from('document_template_library')
        .select('*')
        .eq('is_active', true)
        .order('template_name');
      
      const { data: legacyData } = await supabase
        .from('document_templates')
        .select('*')
        .eq('is_active', true)
        .order('template_name');

      const allTemplates = [
        ...(libraryData || []),
        ...(legacyData || [])
      ];
      
      setTemplates(allTemplates);
      console.log('📚 Loaded', allTemplates.length, 'templates');
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error",
        description: "Failed to load document templates",
        variant: "destructive",
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedTemplateId) {
      toast({
        title: "Pick a sample document",
        description: "Choose a sample document to start from.",
        variant: "destructive",
      });
      return;
    }

    await generateDocument();
  };

  const generateDocument = async () => {
    if (!selectedTemplateId || !unaId) {
      toast({
        title: "Missing information",
        description: "Choose a sample document first.",
        variant: "destructive"
      });
      return;
    }

    // Verify selectedTemplateId is a UUID before proceeding
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedTemplateId);
    if (!isUUID) {
      console.error('❌ Cannot generate: selectedTemplateId is not a UUID:', selectedTemplateId);
      toast({
        title: "Something went wrong",
        description: "That sample could not be opened. Please pick it again.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      console.log('🚀 Generating document with templateId:', selectedTemplateId);

      const response = await supabase.functions.invoke('generate-document', {
        body: {
          taskId,
          templateId: selectedTemplateId,
        },
      });

      if (response.error) throw response.error;

      toast({
        title: "Document Generated",
        description: "Your document has been created successfully",
      });

      onGenerated();
      onClose();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate document",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const displayTemplate = selectedTemplate || templates.find(t => t.id === selectedTemplateId);

  // If template is auto-selected and auto_generate is true, show simplified UI
  const isAutoGenerate = phaseRequirement?.auto_generate && selectedTemplate;

  // If autoSubmit is enabled and we're loading, show only a loading overlay
  if (autoSubmit && loading) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Generating document...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isAutoGenerate ? 'Generate Document' : 'Generate Document'}
            </DialogTitle>
            <DialogDescription>
              {isAutoGenerate 
                ? `Ready to generate: ${phaseRequirement?.document_name || selectedTemplate?.template_name}`
                : 'Choose how to create your document'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {isAutoGenerate ? (
              // Simplified view for auto-generate documents
              <div className="space-y-4">
                <Card className="p-4 bg-muted/50">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 mt-0.5 text-primary" />
                    <div className="flex-1">
                      <h4 className="font-medium mb-1">{selectedTemplate?.template_name}</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        {phaseRequirement?.description || selectedTemplate?.description}
                      </p>
                      {phaseRequirement?.requires_signature && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          ⚠️ This document will require signatures
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
                <p className="text-sm text-muted-foreground">
                  This document will be automatically populated with data from your workflow.
                  Click Generate to create the document.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {loadingTemplates ? (
                  <div className="text-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="template-select" className="text-sm font-medium mb-2 block">
                        Pick a sample document
                      </Label>
                      <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                        <SelectTrigger id="template-select">
                          <SelectValue placeholder="Pick one" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                {template.template_name}
                                {template.is_system && (
                                  <span className="text-xs text-muted-foreground">(System)</span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {displayTemplate && (
                      <Card className="p-3 bg-muted/50">
                        <h4 className="font-medium text-sm mb-1">{displayTemplate.template_name}</h4>
                        <p className="text-xs text-muted-foreground mb-2">
                          {displayTemplate.description}
                        </p>
                        
                        {displayTemplate.merge_fields && displayTemplate.merge_fields.length > 0 && (
                          <div>
                            <p className="text-xs font-medium mb-1">Required Fields:</p>
                            <div className="flex flex-wrap gap-1">
                              {displayTemplate.merge_fields
                                .filter((f: any) => f.required)
                                .map((field: any, idx: number) => (
                                  <span key={idx} className="text-xs px-2 py-0.5 bg-background rounded border">
                                    {field.name}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </Card>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={isAutoGenerate ? generateDocument : handleContinue}
              disabled={loading || !selectedTemplateId}
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isAutoGenerate ? 'Generate Document' : 'Generate'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}
